/**
 * EmployerReviewersAdminPage.jsx — Phase 1.0 admin reviewer roster UI.
 *
 * Surfaces:
 *  - Roster table grouped by role (newest first).
 *  - Assign / Pause / Resume / Unassign actions via the LOCKED-LIVE
 *    shim (Slice C idempotency-keyed).
 *  - Role-aware: admin / recruiter only. Reviewer self → access denied.
 *
 * Out of scope: candidate routing UX (recruiters do that on the Roles
 * page once it integrates the route endpoint); reviewer queue UX
 * (that lives in EmployerReviewsPage.jsx).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Plus, Play, Pause, UserMinus, RefreshCw, Users } from 'lucide-react';

import { apiFetch } from '@/lib/api';
import {
  listReviewerAssignments,
  listReviewerMembers,
  listOpenReviewsByAccount,
} from '@/lib/employerReviewerRoster';
import {
  assignReviewer, pauseAssignment, resumeAssignment, unassignReviewer,
} from '@/lib/employerReviews';
import { t, tn } from '@/lib/employerReviewerStrings';

import { Button }     from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge }      from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';


function genIdempotencyKey() {
  return `rev-roster-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}


export default function EmployerReviewersAdminPage() {
  const navigate = useNavigate();

  const [bootError,   setBootError]    = useState(null);
  const [accountId,   setAccountId]    = useState(null);
  const [myRole,      setMyRole]       = useState(null);
  const [rows,        setRows]         = useState([]);
  const [roles,       setRoles]        = useState([]);
  const [reviewers,   setReviewers]    = useState([]);
  const [workload,    setWorkload]     = useState({}); // reviewer_user_id → { pending, in_review }
  const [loading,     setLoading]      = useState(true);
  const [busyId,      setBusyId]       = useState(null);
  const [assignOpen,  setAssignOpen]   = useState(false);

  const canManage = myRole === 'admin' || myRole === 'recruiter';

  const bootstrap = useCallback(async () => {
    setLoading(true);
    setBootError(null);
    try {
      const me = await apiFetch('/api/employer/v1/me');
      const aid  = me?.account_id ?? me?.organisation?.id;
      const role = me?.member?.role ?? me?.role;
      setAccountId(aid); setMyRole(role);
      if (!aid) throw new Error('No organisation found for current user');
      if (role !== 'admin' && role !== 'recruiter') {
        setLoading(false); return;
      }
      const [assignments, rolesResp, reviewerMembers, openReviews] = await Promise.all([
        listReviewerAssignments(aid),
        apiFetch('/api/employer/roles'),
        listReviewerMembers(aid),
        listOpenReviewsByAccount(aid).catch(() => []),
      ]);
      setRows(assignments);
      setRoles(rolesResp?.items || rolesResp?.roles || rolesResp || []);
      setReviewers(reviewerMembers);

      // Build per-reviewer workload map. Counters are summary-only,
      // grouped client-side by reviewer_user_id + status. Single
      // admin-RLS read; no aggregate endpoint, no cross-user queue
      // access.
      const wl = {};
      for (const r of openReviews || []) {
        const uid = r.reviewer_user_id;
        if (!uid) continue;
        if (!wl[uid]) wl[uid] = { pending: 0, in_review: 0 };
        if (r.status === 'pending')   wl[uid].pending   += 1;
        if (r.status === 'in_review') wl[uid].in_review += 1;
      }
      setWorkload(wl);
    } catch (e) {
      setBootError(e?.message || t('admin.roster.bootstrap.fail'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  const roleMap = useMemo(() => {
    const map = new Map();
    for (const r of roles) map.set(r.id, r);
    return map;
  }, [roles]);

  const reviewerLabel = (uid) => {
    const m = reviewers.find(r => r.user_id === uid);
    if (m) return `${uid.slice(0, 8)}\u2026`;
    return uid?.slice(0, 8) + '\u2026';
  };

  const handlePause = async (row) => {
    setBusyId(row.assignment_id);
    try {
      await pauseAssignment({
        accountId, assignmentId: row.assignment_id, idempotencyKey: genIdempotencyKey(),
      });
      toast.success(t('admin.roster.toast.pause.success'));
      bootstrap();
    } catch (e) { toast.error(e?.message || t('admin.roster.toast.pause.fail')); }
    finally { setBusyId(null); }
  };

  const handleResume = async (row) => {
    setBusyId(row.assignment_id);
    try {
      await resumeAssignment({
        accountId, assignmentId: row.assignment_id, idempotencyKey: genIdempotencyKey(),
      });
      toast.success(t('admin.roster.toast.resume.success'));
      bootstrap();
    } catch (e) { toast.error(e?.message || t('admin.roster.toast.resume.fail')); }
    finally { setBusyId(null); }
  };

  const handleUnassign = async (row) => {
    if (!window.confirm(t('admin.roster.unassign.confirm'))) return;
    setBusyId(row.assignment_id);
    try {
      await unassignReviewer({
        accountId, assignmentId: row.assignment_id, idempotencyKey: genIdempotencyKey(),
      });
      toast.success(t('admin.roster.toast.unassign.success'));
      bootstrap();
    } catch (e) { toast.error(e?.message || t('admin.roster.toast.unassign.fail')); }
    finally { setBusyId(null); }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground" data-testid="reviewer-roster-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> {t('admin.roster.loading')}
      </div>
    );
  }

  if (bootError) {
    return (
      <div className="p-6" data-testid="reviewer-roster-boot-error">
        <Card><CardContent className="p-4 text-sm text-rose-600">{bootError}</CardContent></Card>
      </div>
    );
  }

  if (!canManage) {
    return (
      <div className="p-6" data-testid="reviewer-roster-denied">
        <Card>
          <CardContent className="p-4 text-sm text-muted-foreground">
            {t('admin.roster.access.denied')}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-6" data-testid="reviewer-roster-page">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">{t('admin.roster.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('admin.roster.subtitle')}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" data-testid="reviewer-roster-refresh" onClick={bootstrap}>
            <RefreshCw className="mr-1 h-3 w-3" /> {t('admin.roster.action.refresh')}
          </Button>
          <Button size="sm" data-testid="reviewer-roster-assign-open"
                  onClick={() => setAssignOpen(true)}
                  disabled={roles.length === 0 || reviewers.length === 0}>
            <Plus className="mr-1 h-3 w-3" /> {t('admin.roster.action.assign')}
          </Button>
        </div>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Users className="h-4 w-4" />
            {t('admin.roster.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <div
              data-testid="reviewer-roster-empty"
              className="py-10 text-center space-y-3 max-w-md mx-auto"
            >
              <Users className="mx-auto h-8 w-8 text-muted-foreground/70" />
              <p className="text-sm font-medium text-foreground">
                {t('admin.roster.empty')}
              </p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {t('admin.roster.empty.body')}
              </p>
              <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
                <Button
                  asChild
                  variant="outline"
                  size="sm"
                  data-testid="reviewer-roster-empty-cta-members"
                >
                  <a href="/employer/members">{t('admin.roster.empty.cta.members')}</a>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  size="sm"
                  data-testid="reviewer-roster-empty-cta-roles"
                >
                  <a href="/employer/roles">{t('admin.roster.empty.cta.roles')}</a>
                </Button>
                {roles.length > 0 && reviewers.length > 0 && (
                  <Button
                    size="sm"
                    data-testid="reviewer-roster-empty-cta-assign"
                    onClick={() => setAssignOpen(true)}
                  >
                    <Plus className="mr-1 h-3 w-3" /> {t('admin.roster.action.assign')}
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="reviewer-roster-table">
                <thead>
                  <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                    <th className="py-2 pr-3">{t('admin.roster.col.role')}</th>
                    <th className="py-2 pr-3">{t('admin.roster.col.reviewer')}</th>
                    <th className="py-2 pr-3">{t('admin.roster.col.status')}</th>
                    <th className="py-2 pr-3 hidden md:table-cell">{t('admin.roster.col.workload')}</th>
                    <th className="py-2 pr-3 hidden sm:table-cell">{t('admin.roster.col.created')}</th>
                    <th className="py-2 pr-3">{t('admin.roster.col.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const role = roleMap.get(r.role_id);
                    const statusKey =
                      r.assignment_status === 'active'  ? 'admin.roster.status.active'  :
                      r.assignment_status === 'paused'  ? 'admin.roster.status.paused'  :
                                                          'admin.roster.status.removed';
                    const statusVariant =
                      r.assignment_status === 'active'  ? 'default'   :
                      r.assignment_status === 'paused'  ? 'secondary' : 'outline';
                    const isRemoved = r.assignment_status === 'removed';
                    const wl = workload[r.reviewer_user_id] || { pending: 0, in_review: 0 };
                    const wlEmpty = wl.pending === 0 && wl.in_review === 0;
                    return (
                      <tr key={r.assignment_id} className="border-b last:border-0"
                          data-testid={`reviewer-roster-row-${r.assignment_id}`}>
                        <td className="py-2 pr-3">
                          <div className="font-medium">{role?.title ?? r.role_id.slice(0, 8) + '\u2026'}</div>
                          <div className="text-xs text-muted-foreground hidden sm:block">{r.role_id.slice(0, 8)}…</div>
                        </td>
                        <td className="py-2 pr-3 font-mono text-xs">
                          {reviewerLabel(r.reviewer_user_id)}
                        </td>
                        <td className="py-2 pr-3">
                          <Badge variant={statusVariant}>{t(statusKey)}</Badge>
                        </td>
                        <td
                          className="py-2 pr-3 hidden md:table-cell text-xs"
                          data-testid={`reviewer-roster-workload-${r.assignment_id}`}
                        >
                          {isRemoved ? (
                            <span className="text-muted-foreground">{'\u2014'}</span>
                          ) : wlEmpty ? (
                            <span className="text-muted-foreground">
                              {t('admin.roster.workload.none')}
                            </span>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {wl.pending > 0 && (
                                <Badge variant="secondary"
                                       data-testid={`reviewer-roster-workload-pending-${r.assignment_id}`}>
                                  {tn('admin.roster.workload.pending', { n: wl.pending })}
                                </Badge>
                              )}
                              {wl.in_review > 0 && (
                                <Badge variant="default"
                                       data-testid={`reviewer-roster-workload-in-review-${r.assignment_id}`}>
                                  {tn('admin.roster.workload.in_review', { n: wl.in_review })}
                                </Badge>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-xs text-muted-foreground hidden sm:table-cell">
                          {r.created_at ? new Date(r.created_at).toLocaleString() : '\u2014'}
                        </td>
                        <td className="py-2 pr-3">
                          <div className="flex flex-wrap gap-2">
                            {r.assignment_status === 'active' && (
                              <Button size="sm" variant="outline"
                                      data-testid={`reviewer-roster-pause-${r.assignment_id}`}
                                      disabled={busyId === r.assignment_id}
                                      onClick={() => handlePause(r)}>
                                <Pause className="mr-1 h-3 w-3" /> {t('admin.roster.action.pause')}
                              </Button>
                            )}
                            {r.assignment_status === 'paused' && (
                              <Button size="sm" variant="outline"
                                      data-testid={`reviewer-roster-resume-${r.assignment_id}`}
                                      disabled={busyId === r.assignment_id}
                                      onClick={() => handleResume(r)}>
                                <Play className="mr-1 h-3 w-3" /> {t('admin.roster.action.resume')}
                              </Button>
                            )}
                            {!isRemoved && (
                              <Button size="sm" variant="ghost"
                                      data-testid={`reviewer-roster-unassign-${r.assignment_id}`}
                                      disabled={busyId === r.assignment_id}
                                      onClick={() => handleUnassign(r)}>
                                <UserMinus className="mr-1 h-3 w-3" /> {t('admin.roster.action.unassign')}
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <AssignDialog
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        onDone={bootstrap}
        accountId={accountId}
        roles={roles}
        reviewers={reviewers}
      />
    </div>
  );
}


function AssignDialog({ open, onClose, onDone, accountId, roles, reviewers }) {
  const [roleId,      setRoleId]   = useState('');
  const [reviewerId,  setReviewer] = useState('');
  const [busy,        setBusy]     = useState(false);

  useEffect(() => {
    if (open) { setRoleId(''); setReviewer(''); }
  }, [open]);

  const submit = async () => {
    if (!roleId || !reviewerId || !accountId) return;
    setBusy(true);
    try {
      await assignReviewer({
        accountId,
        roleId,
        reviewerUserId: reviewerId,
        idempotencyKey:  genIdempotencyKey(),
      });
      toast.success(t('admin.assign.toast.success'));
      onClose(); onDone();
    } catch (e) { toast.error(e?.message || t('admin.assign.toast.fail')); }
    finally { setBusy(false); }
  };

  const noRoles      = roles.length === 0;
  const noReviewers  = reviewers.length === 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="reviewer-roster-assign-dialog">
        <DialogHeader>
          <DialogTitle>{t('admin.assign.title')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <div className="mb-1 text-sm font-medium">{t('admin.assign.field.role')}</div>
            {noRoles ? (
              <div className="text-sm text-rose-600" data-testid="reviewer-roster-no-roles">
                {t('admin.assign.no_roles')}
              </div>
            ) : (
              <Select value={roleId} onValueChange={setRoleId}>
                <SelectTrigger data-testid="reviewer-roster-role-select">
                  <SelectValue placeholder={t('admin.assign.placeholder.role')} />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((r) => (
                    <SelectItem key={r.id} value={r.id}>{r.title || r.id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div>
            <div className="mb-1 text-sm font-medium">{t('admin.assign.field.reviewer')}</div>
            {noReviewers ? (
              <div className="text-sm text-rose-600" data-testid="reviewer-roster-no-reviewers">
                {t('admin.assign.no_reviewers')}
              </div>
            ) : (
              <Select value={reviewerId} onValueChange={setReviewer}>
                <SelectTrigger data-testid="reviewer-roster-reviewer-select">
                  <SelectValue placeholder={t('admin.assign.placeholder.reviewer')} />
                </SelectTrigger>
                <SelectContent>
                  {reviewers.map((m) => (
                    <SelectItem key={m.user_id} value={m.user_id}>
                      {m.user_id.slice(0, 8)}…
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <p className="mt-1 text-xs text-muted-foreground">{t('admin.assign.help.reviewer')}</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            {t('admin.assign.cancel')}
          </Button>
          <Button data-testid="reviewer-roster-assign-submit"
                  onClick={submit}
                  disabled={busy || !roleId || !reviewerId || noRoles || noReviewers}>
            {busy && <Loader2 className="mr-1 h-3 w-3 animate-spin" />} {t('admin.assign.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
